import React, { ReactNode, forwardRef } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';

import { TourService } from '~/types/tours/costPrice';
import { costPriceTypeLabel } from '~/types/tours/supplier';

import { vendorPath } from '../../nativeTourModel';

import TourServiceOptionLabel from './TourServiceOptionLabel';
import { formatAmount } from './formatAmount';

interface Props {
  service: TourService;
  expanded: boolean;
  onToggle: () => void;
  /** This supplier's cost for the selected departure; absent when no departure is selected. */
  cost?: {
    totalCost: number | null;
    currency: string | null;
    units: number;
    convertedCost?: number;
    isConverting?: boolean;
  };
  /** Set when the service's pricing can be uploaded in place, rather than from its own view. */
  uploadLabel?: string;
  onUpload?: () => void;
  onManagePricing: () => void;
  children: ReactNode;
}

function costLine(cost: NonNullable<Props['cost']>): string {
  if (!cost.units) {
    return 'No travellers on this departure';
  }

  const aud = cost.isConverting
    ? 'converting to AUD…'
    : cost.convertedCost === undefined
      ? 'AUD unavailable'
      : `$${formatAmount(cost.convertedCost)} AUD`;

  return `${formatAmount(cost.totalCost)} ${cost.currency ?? ''} · ${aud} · ${cost.units} travellers`;
}

const SupplierCostSection = forwardRef<HTMLDivElement, Props>(function SupplierCostSection(
  { service, expanded, onToggle, cost, uploadLabel, onUpload, onManagePricing, children },
  ref,
) {
  // The actions sit in the summary; keep them from toggling the section.
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <Accordion ref={ref} expanded={expanded} onChange={onToggle} disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          sx={{ width: '100%', pr: 1 }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              <TourServiceOptionLabel service={service} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cost ? costLine(cost) : costPriceTypeLabel(service.costPriceType)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} onClick={stop} onFocus={stop}>
            {onUpload ? (
              <Button size="small" variant="outlined" onClick={onUpload}>
                {uploadLabel}
              </Button>
            ) : (
              <Button size="small" variant="outlined" onClick={onManagePricing}>
                Manage pricing
              </Button>
            )}
            {!!service.sourceSupplierId && (
              <Button size="small" component={RouterLink} to={vendorPath(service.sourceSupplierId)}>
                View vendor
              </Button>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
});

export default SupplierCostSection;
